/**
 * Per-player palette data shared by the map layers: 256×256 RGBA8 entries
 * indexed by player id (id & 255, id >> 8). RGB = player colour, A = relationship
 * flags relative to the focus player (see PALETTE_FLAG_* in paletteFlags.ts).
 */
import type { PlayerID } from "../../core/types";
import type { IGameView } from "../state/types";
import type { PlayerColors } from "./colors";
import { PALETTE_FLAG_FRIEND, PALETTE_FLAG_TRAITOR, PALETTE_FLAG_WAR } from "./paletteFlags";

export const PALETTE_WIDTH = 256;
export const PALETTE_HEIGHT = 256;

export class PaletteData {
  /** RGBA8, PALETTE_WIDTH × PALETTE_HEIGHT. */
  readonly data = new Uint8Array(PALETTE_WIDTH * PALETTE_HEIGHT * 4);
  /** Dirty row range [dirtyRow0, dirtyRow1) since the last `clearDirty`. */
  dirtyRow0 = 0;
  dirtyRow1 = PALETTE_HEIGHT;
  /** Number of rows that contain any player. */
  usedRows = 1;
  /** True when some player is at war with the focus player (borders pulse). */
  anyWar = false;
  version = 0;

  private readonly scratchWar = new Set<PlayerID>();

  clearDirty(): void {
    this.dirtyRow0 = PALETTE_HEIGHT;
    this.dirtyRow1 = 0;
  }

  isDirty(): boolean {
    return this.dirtyRow1 > this.dirtyRow0;
  }

  /** Mark the whole palette for re-upload (e.g. after a context restore). */
  markAllDirty(): void {
    this.dirtyRow0 = 0;
    this.dirtyRow1 = PALETTE_HEIGHT;
  }

  /**
   * Rebuild all player entries from the view. Only rows whose bytes changed are
   * marked dirty, so steady-state updates are nearly free.
   */
  rebuild(view: IGameView, colors: PlayerColors, focus: PlayerID): void {
    const tick = view.tick();
    const focusPlayer = focus > 0 ? view.player(focus) : undefined;
    const war = this.scratchWar;
    war.clear();
    if (focusPlayer) {
      for (const a of focusPlayer.outgoingAttacks) if (a.target > 0) war.add(a.target);
      for (const a of focusPlayer.incomingAttacks) if (a.attacker > 0) war.add(a.attacker);
    }
    this.anyWar = war.size > 0;
    const d = this.data;
    let changed = false;
    for (const p of view.players()) {
      const id = p.id;
      if (id <= 0 || id >= PALETTE_WIDTH * PALETTE_HEIGHT) continue;
      const c = colors.rgb(id);
      let flags = 0;
      if (focus > 0 && id !== focus) {
        if (view.isFriendly(focus, id)) flags |= PALETTE_FLAG_FRIEND;
        if (war.has(id)) flags |= PALETTE_FLAG_WAR;
      }
      if (p.traitorUntilTick > tick) flags |= PALETTE_FLAG_TRAITOR;
      const o = id * 4;
      if (d[o] !== c.r || d[o + 1] !== c.g || d[o + 2] !== c.b || d[o + 3] !== flags) {
        d[o] = c.r;
        d[o + 1] = c.g;
        d[o + 2] = c.b;
        d[o + 3] = flags;
        const row = id >> 8;
        if (row < this.dirtyRow0) this.dirtyRow0 = row;
        if (row + 1 > this.dirtyRow1) this.dirtyRow1 = row + 1;
        if (row + 1 > this.usedRows) this.usedRows = row + 1;
        changed = true;
      }
    }
    if (changed) this.version++;
  }

  /** RGB of an entry (0..255) — used by the canvas fallback. */
  rgbAt(id: PlayerID, out: [number, number, number]): [number, number, number] {
    const o = id * 4;
    out[0] = this.data[o];
    out[1] = this.data[o + 1];
    out[2] = this.data[o + 2];
    return out;
  }

  flagsAt(id: PlayerID): number {
    return this.data[id * 4 + 3];
  }
}
